import { useEffect, useRef, useState, type ReactNode } from 'react';
import './Toast.css';

/**
 * DESIGN.md §10 Toast: inverse surface above the tab bar, one line + one action. Auto-dismisses
 * after `duration` ms (paused while hovered or focused); `duration={null}` stays until acted on.
 */
export function Toast({
  children,
  action,
  onDismiss,
  duration = 4000,
}: {
  children: ReactNode;
  action?: ReactNode;
  onDismiss?: () => void;
  duration?: number | null;
}) {
  const [paused, setPaused] = useState(false);
  const dismiss = useRef(onDismiss);
  useEffect(() => {
    dismiss.current = onDismiss;
  });

  useEffect(() => {
    if (duration === null || paused) return;
    const timer = setTimeout(() => dismiss.current?.(), duration);
    return () => clearTimeout(timer);
  }, [duration, paused]);

  return (
    <div
      className="toast"
      role="status"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <div className="toast__text">{children}</div>
      {action}
    </div>
  );
}

/** A Toast action button (Undo, View, Reload). */
export function ToastAction({ children, onClick }: { children: ReactNode; onClick: () => void }) {
  return (
    <button type="button" className="toast__action" onClick={onClick}>
      {children}
    </button>
  );
}

/** Fixed region above the tab bar where toasts appear, one at a time. */
export function ToastRegion({ children }: { children: ReactNode }) {
  return <div className="toast-region">{children}</div>;
}
